import { Code as GeneratedCode, type ValidationCode } from './validationCodes.gen';

/**
 * FE-only codes. The global error handler emits these for generic Pydantic
 * errors (type coerce failures, missing fields) that don't map to a domain
 * ValidationException. Kept separate from the generated backend catalog.
 */
const GenericCodes = {
  FieldRequired: 'field_required',
  FieldInvalid: 'field_invalid',
} as const;

export type GenericCode = (typeof GenericCodes)[keyof typeof GenericCodes];

export const Code = {
  ...GeneratedCode,
  Generic: GenericCodes,
} as const;

export type { ValidationCode };

/**
 * Every code FE knows how to render. Includes both backend codes (generated
 * from the Code catalog) and FE-only Generic codes. Used to give
 * `messageForKnownCode` an exhaustiveness check at the type level.
 */
export type KnownCode = ValidationCode | GenericCode;

export interface FieldError {
  code: string;
  field: string | null;
  params?: Record<string, unknown>;
}

/** Return user-facing copy for a single field error. */
export function messageForCode(err: FieldError): string {
  if (isKnownCode(err.code)) {
    return messageForKnownCode(err.code, err);
  }
  return "something's not right — double-check your entries";
}

/**
 * Exhaustive over `KnownCode`. Adding a new code to the backend will fail the
 * build here until a `case` is added, eliminating silent fallbacks for
 * codes the FE forgot to ship copy for.
 */
function messageForKnownCode(code: KnownCode, err: FieldError): string {
  switch (code) {
    // Event
    case Code.Event.NotFound:
      return 'event not found';
    case Code.Event.StartDatetimeRequiredUnlessTbd:
      return 'pick a start time, or mark the time as tbd';
    case Code.Event.MaxAttendeesMustBeAtLeastOne:
      return 'max attendees must be at least 1 — leave blank for unlimited';
    case Code.Event.StartDatetimeMustBeFuture:
      return 'start date must be in the future';
    case Code.Event.EndBeforeStart:
      return 'end time must be after the start time';
    case Code.Event.DatetimeOffsetRequired:
      return 'date and time must include a timezone';
    case Code.Event.OfficialMustBePublic:
      return 'official events must be public';
    case Code.Event.OfficialNoPlusOnes:
      return "official and club events can't allow +1s";
    case Code.Event.InvalidCreateStatus:
      return 'new events can only be saved as active or draft';
    case Code.Event.DateLockedByPoll:
      return "can't edit the date while a poll is active — finalize the poll first";
    case Code.Event.PermDenied:
      return "you don't have permission to see this event";
    case Code.Event.AuthRequired:
      return 'you need to sign in for that';
    case Code.Event.CancelledCannotBeEdited:
      return "cancelled events can't be edited";
    case Code.Event.PastCannotBeCancelled:
      return "past events can't be cancelled — delete instead";
    case Code.Event.NoAttendeesCannotBeCancelled:
      return "events with no invited users or rsvps can't be cancelled — delete instead";
    case Code.Event.InvalidStatusTransition:
      return 'invalid status change';
    case Code.Event.HasRsvps:
      return "can't send this event back to draft — people have already rsvp'd";
    case Code.Event.PastCannotBeUnpublished:
      return "past events can't go back to draft";
    case Code.Event.CancelBeforeDelete:
      return 'cancel this event before deleting it';
    case Code.Event.RsvpInvalidStatus:
      return 'invalid rsvp status';
    case Code.Event.FlagAlreadyFlagged:
      return "you've already flagged this event";
    case Code.Event.FlagInvalidAction:
      return 'invalid flag action';
    case Code.Event.RsvpsNotEnabled:
      return 'rsvps are not enabled for this event';
    case Code.Event.RsvpsClosedCancelled:
      return 'rsvps are closed for cancelled events';
    case Code.Event.RsvpsClosedPast:
      return 'rsvps are closed for past events';
    case Code.Event.NoPlusOneSpots:
      return 'no spots available for a +1';
    case Code.Event.RsvpNotFound:
      return 'rsvp not found';
    case Code.Event.PaymentVerifyNotGuests:
      return "some of those people aren't attending or waitlisted for this event — nothing was verified";
    case Code.Event.WaitlistOrderStale:
      return 'the waitlist changed — refresh and try again';
    case Code.Event.MemberContactMustSignIn:
      return 'looks like you already have an account — sign in to rsvp';
    case Code.Event.RsvpCouldNotBeCreated:
      return "we couldn't set up your rsvp with those details — reach out and we'll help";
    case Code.Event.AttendanceOpensLater:
      return 'check-in opens an hour before the event starts';
    case Code.Event.NoPlusOneToCheckIn:
      return "this guest doesn't have a +1 to check in";
    case Code.Event.BlastInvalidAudience:
      return 'that audience choice is not valid';
    case Code.Event.BlastNoRecipients:
      return 'no attendees in that audience have an email — nothing to send';
    case Code.Event.WouldRemoveNonMembers:
      return "this change would remove non-members rsvp'd on this event";
    case Code.Event.CheckInReportNotYetAvailable:
      return 'the check-in report is available once the event has ended';
    case Code.Event.CheckInReportInvalidColumn:
      return "one of those csv columns isn't recognized";
    case Code.Event.SlugInvalid:
      return 'event url can only use letters, numbers, and dashes — start and end with a letter or number';
    case Code.Event.SlugTaken:
      return 'that event url is already taken — try another';
    case Code.Event.RsvpQuestionNotFound:
      return 'that rsvp question was not found';
    case Code.Event.RsvpQuestionDuplicate:
      return 'duplicate rsvp question';
    case Code.Event.RsvpQuestionConflict:
      return 'rsvp questions changed — refresh and try again';
    case Code.Event.RsvpQuestionOptionsRequired:
      return 'add at least one option';
    case Code.Event.RsvpAnswerRequired: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `"${label}" is required` : 'an answer is required';
    }
    case Code.Event.RsvpAnswerInvalidOption: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `invalid option for "${label}"` : 'invalid option';
    }
    case Code.Event.RsvpAnswerInvalidFormat: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `invalid answer format for "${label}"` : 'invalid answer format';
    }
    case Code.Event.RsvpAnswerTooLong: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      const max = typeof err.params?.max === 'number' ? err.params.max : null;
      if (label && max !== null) return `"${label}" must be at most ${String(max)} characters`;
      return 'that answer is too long';
    }
    case Code.Event.PaymentConfirmationRequired:
      return 'confirm you paid before rsvping to this event';

    // Poll
    case Code.Poll.NotFound:
      return 'poll not found';
    case Code.Poll.OptionsRequired:
      return 'a poll requires at least 1 option';
    case Code.Poll.OptionsMustBeFuture:
      return 'poll options must be in the future';
    case Code.Poll.EventAlreadyHasPoll:
      return 'this event already has a poll';
    case Code.Poll.OptionNotFound:
      return 'poll option not found';
    case Code.Poll.OptionAlreadyExists:
      return 'that time is already an option in this poll';
    case Code.Poll.CannotModifyFinalized:
      return 'cannot modify a finalized poll';
    case Code.Poll.AlreadyFinalized:
      return 'this poll has already been finalized';
    case Code.Poll.WinningOptionNotFound:
      return 'winning option not found in this poll';
    case Code.Poll.MinTwoOptions:
      return 'a poll must have at least 2 options';
    case Code.Poll.TooManyOptions: {
      const max = typeof err.params?.max === 'number' ? err.params.max : null;
      return max !== null
        ? `a poll can have at most ${String(max)} options`
        : 'too many poll options';
    }
    case Code.Poll.InvalidAvailability:
      return 'availability must be "yes", "maybe", or "no"';

    // Comment
    case Code.Comment.NotFound:
      return 'comment not found';
    case Code.Comment.ReplyDepthExceeded:
      return "replies can't have replies";
    case Code.Comment.InvalidEmoji:
      return 'that emoji is not available';
    case Code.Comment.RsvpRequired:
      return 'rsvp to join the conversation';
    case Code.Comment.PermDenied:
      return "you don't have permission to do that";
    case Code.Comment.EventMismatch:
      return "that reply target isn't in this event";

    // Tag
    case Code.Tag.NotFound:
      return 'tag not found';
    case Code.Tag.NameRequired:
      return 'enter a tag name';
    case Code.Tag.NameAlreadyExists:
      return 'a tag with that name already exists';

    // URL
    case Code.Url.Invalid:
      return 'enter a valid url';
    case Code.Url.TooLong: {
      const max = typeof err.params?.max_length === 'number' ? err.params.max_length : null;
      return max !== null ? `link must be at most ${String(max)} characters` : 'link is too long';
    }
    case Code.Url.PathRequired:
      return 'link must point to a specific page, not just a homepage';
    case Code.Url.SchemeMustBeHttpOrHttps:
      return 'url must start with http:// or https://';
    case Code.Url.WhatsappNotRecognized:
      return 'whatsapp link must be from chat.whatsapp.com, wa.me, or whats.app';
    case Code.Url.PartifulNotRecognized:
      return 'partiful link must be a partiful.com url';

    // Phone
    case Code.Phone.Invalid:
      return "that doesn't look like a valid phone number";
    case Code.Phone.AlreadyExists:
      return 'a member with that phone number already exists';

    // Email
    case Code.Email.Invalid:
      return "that doesn't look like a valid email";
    case Code.Email.AlreadyExists:
      return 'that email is already on another account — try a different one or contact admin';
    case Code.Email.Required:
      return 'email required';

    // Zelle
    case Code.Zelle.Invalid:
      return 'zelle must be an email address or phone number';

    // Display name
    case Code.DisplayName.Required:
      return 'display name is required';
    case Code.DisplayName.TooLong: {
      const max = typeof err.params?.max_length === 'number' ? err.params.max_length : null;
      return max !== null
        ? `display name must be at most ${String(max)} characters`
        : 'display name is too long';
    }
    case Code.DisplayName.InvalidChars:
      return 'display name can only have letters, spaces, apostrophes, hyphens, and periods';
    case Code.DisplayName.NeedsALetter:
      return 'display name must contain at least one letter';

    // Auth
    case Code.Auth.InvalidCredentials:
      return "that phone number and password don't match — try again";
    case Code.Auth.AccountArchived:
      return 'this account is no longer active';
    case Code.Auth.AccountPaused:
      return 'your membership is currently paused';
    case Code.Auth.MagicLinkInvalidOrExpired:
      return 'this login link is invalid or has expired';
    case Code.Auth.MagicLinkAlreadyUsed:
      return 'this login link has already been used';
    case Code.Auth.AlreadySignedInAsDifferentUser:
      return "you're signed in as a different user — log out first";
    case Code.Auth.RefreshTokenInvalid:
    case Code.Auth.RefreshFailed:
      return 'your session expired — please sign in again';
    case Code.Auth.CurrentPasswordIncorrect:
      return "current password doesn't match";
    case Code.Auth.PasswordResetRequired:
      return 'please set a new password to continue';
    case Code.Auth.OnboardingRequired:
      return 'finish setting up your account to continue';
    case Code.Auth.GuidelinesConsentRequired:
      return 'please agree to the community guidelines to continue';

    // Password
    case Code.Password.Invalid: {
      const reasons = extractStringArray(err.params, 'reasons');
      if (reasons.length > 0) return reasons.join(' · ');
      return 'password is not strong enough';
    }
    case Code.Password.SameAsOld:
      return 'new password must be different from your current one';

    // Role
    case Code.Role.NotFound:
      return 'role not found';
    case Code.Role.NameAlreadyExists:
      return 'a role with that name already exists';
    case Code.Role.ProtectedCannotEdit: {
      const name = typeof err.params?.role_name === 'string' ? err.params.role_name : null;
      return name
        ? `the "${name}" role is built-in and can't be edited`
        : "this role is built-in and can't be edited";
    }
    case Code.Role.ProtectedCannotRename: {
      const name = typeof err.params?.role_name === 'string' ? err.params.role_name : null;
      return name
        ? `the "${name}" role is built-in and can't be renamed`
        : "this role is built-in and can't be renamed";
    }
    case Code.Role.ProtectedCannotDelete: {
      const name = typeof err.params?.role_name === 'string' ? err.params.role_name : null;
      return name
        ? `the "${name}" role is built-in and can't be deleted`
        : "this role is built-in and can't be deleted";
    }
    case Code.Role.CannotRemoveOwnAdmin:
      return "you can't remove your own admin role";
    case Code.Role.CannotRemoveLastAdmin:
      return "can't remove admin from the last admin — promote someone else first";
    case Code.Role.CannotGrantAdmin:
      return 'only an admin can grant the admin role';
    case Code.Role.InvalidPermission: {
      const perm = typeof err.params?.permission === 'string' ? err.params.permission : null;
      return perm ? `unknown permission: ${perm}` : 'one or more permissions are invalid';
    }
    case Code.Role.MemberRoleRequired:
      return 'every user must keep the member role';

    // Member
    case Code.Member.NotFound:
      return 'member not found';

    // User (admin actions)
    case Code.User.NotFound:
      return 'user not found';
    case Code.User.CannotDeleteSelf:
      return "you can't delete your own account";
    case Code.User.CannotDeleteLastAdmin:
      return "can't delete the last admin — promote someone else first";
    case Code.User.AlreadyArchived:
      return 'this user is already archived';
    case Code.User.CannotHardDeleteLoggedIn:
      return 'this member has logged in — archive them instead of deleting';
    case Code.User.CannotPauseSelf:
      return "you can't pause your own account";
    case Code.User.CannotPauseAdmin:
      return "admins can't be paused";
    case Code.User.RoleIdsNotFound:
      return 'one or more role IDs not found';
    case Code.User.InvalidBirthday:
      return "that's not a valid birthday";
    case Code.User.InvalidVeganniversary:
      return "that's not a valid veganniversary";

    // Survey
    case Code.Survey.NotFound:
      return 'survey not found';
    case Code.Survey.Closed:
      return 'this survey is closed — responses are no longer accepted';
    case Code.Survey.SlugAlreadyExists:
      return 'a survey with that slug already exists';
    case Code.Survey.ClosesBeforeOpens:
      return 'closes at must be after opens at';
    case Code.Survey.QuestionNotFound:
      return 'question not found';
    case Code.Survey.ResponseNotFound:
      return 'response not found';
    case Code.Survey.AnonymousLocked:
      return "anonymous can't be changed once a survey has responses";
    case Code.Survey.FeedbackTypeTaken:
      return 'another survey is already the feedback survey for that event type';
    case Code.Survey.FeedbackLinkInvalid:
      return "this feedback link doesn't work anymore";
    case Code.Survey.FeedbackAlreadySubmitted:
      return 'you already sent feedback for this event — thank you!';
    case Code.Survey.AnswerRequired: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `"${label}" is required` : 'an answer is required';
    }
    case Code.Survey.AnswerInvalidFormat: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `invalid answer format for "${label}"` : 'invalid answer format';
    }
    case Code.Survey.AnswerInvalidOption: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `invalid option for "${label}"` : 'invalid option';
    }
    case Code.Survey.AnswerMustBeNumber: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `"${label}" must be a number` : 'must be a number';
    }
    case Code.Survey.AnswerMustBeBoolean: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `"${label}" must be yes or no` : 'must be yes or no';
    }
    case Code.Survey.AnswerRatingOutOfRange: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `"${label}" must be between 1 and 5` : 'rating must be between 1 and 5';
    }
    case Code.Survey.AnswerInvalidDatetimeOption: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `invalid datetime option for "${label}"` : 'invalid datetime option';
    }
    case Code.Survey.AnswerInvalidAvailability: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label
        ? `availability for "${label}" must be "yes", "maybe", or "no"`
        : 'availability must be "yes", "maybe", or "no"';
    }
    case Code.Survey.ConditionQuestionNotFound:
      return 'the question this condition depends on no longer exists';
    case Code.Survey.ConditionQuestionNotEarlier:
      return 'a condition can only depend on an earlier question';
    case Code.Survey.ConditionTypeNotSupported: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label
        ? `"${label}" can't be used in a condition — pick a choice or yes/no question`
        : "that question can't be used in a condition";
    }
    case Code.Survey.ConditionOperatorNotSupported:
      return '"includes" only works with checkbox questions';
    case Code.Survey.ConditionValueInvalid: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `pick one of the options from "${label}"` : 'pick a valid option';
    }
    case Code.Survey.ConditionOrderConflict: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label
        ? `"${label}" must stay below the question its condition depends on`
        : 'a question must stay below the question its condition depends on';
    }
    case Code.Survey.ConditionSourceInUse: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label
        ? `this change would break the condition on ${label} — update or remove that condition first`
        : 'this change would break a condition on a later question — update or remove it first';
    }

    // Join request
    case Code.JoinRequest.NotFound:
      return 'join request not found';
    case Code.JoinRequest.AlreadyDecided:
      return 'this request has already been decided';
    case Code.JoinRequest.OnlyRejectedCanBeUnRejected:
      return 'only rejected requests can be un-rejected';
    case Code.JoinRequest.AlreadyMember:
      return "you're already a member — try logging in instead";
    case Code.JoinRequest.PhoneAlreadyInvited:
      return 'that number is already in the community — try logging in instead';
    case Code.JoinRequest.PhoneAlreadyPending:
      return "a request for this number is already pending — we'll be in touch soon";
    case Code.JoinRequest.PhoneAlreadyTentative:
      return 'you already have an account — try logging in instead';
    case Code.JoinRequest.AnswerRequired: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `"${label}" is required` : 'an answer is required';
    }
    case Code.JoinRequest.AnswerTooLong: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      const max = typeof err.params?.max === 'number' ? err.params.max : null;
      if (label && max !== null) return `"${label}" must be at most ${String(max)} characters`;
      return 'that answer is too long';
    }
    case Code.JoinRequest.AnswerInvalidOption: {
      const label = typeof err.params?.label === 'string' ? err.params.label : null;
      return label ? `invalid option for "${label}"` : 'invalid option';
    }
    case Code.JoinRequest.InvalidStatus:
      return 'invalid status for this action';
    case Code.JoinRequest.NotApproved:
      return 'this request needs to be approved first';
    case Code.JoinRequest.AlreadyLoggedIn:
      return "they've already logged in — re-sending a welcome link won't help";
    case Code.JoinRequest.SmsConsentRequired:
      return 'please agree to receive sms about events to submit';
    case Code.JoinRequest.GuidelinesConsentRequired:
      return 'please read and confirm you agree to the guidelines and community agreements to submit';
    case Code.JoinRequest.InvalidRejectionReason:
      return 'pick a rejection reason from the list';
    case Code.JoinRequest.RejectionNoteRequired:
      return 'write a reason, or pick one from the list';
    case Code.JoinRequest.RejectionNoteTooLong: {
      const max = typeof err.params?.max_length === 'number' ? err.params.max_length : null;
      return max !== null
        ? `reason must be at most ${String(max)} characters`
        : 'reason is too long';
    }

    // Photo
    case Code.Photo.TypeNotAllowed:
      return 'photo must be a jpeg, png, webp, or gif';
    case Code.Photo.TooLarge: {
      const maxMb = typeof err.params?.max_mb === 'number' ? err.params.max_mb : null;
      return maxMb !== null ? `photo must be under ${String(maxMb)} mb` : 'photo is too large';
    }
    case Code.Photo.Unsafe:
      return "that photo can't be processed — try a smaller image";

    // Attendance import
    case Code.AttendanceImport.CsvEmpty:
      return "that csv doesn't have any rows — export attendees from partiful and try again";
    case Code.AttendanceImport.CsvMalformed:
      return "couldn't read that csv — make sure it's the raw partiful export";
    case Code.AttendanceImport.EventOrTitleRequired:
      return 'pick an existing event or give the new event a name and date';
    case Code.AttendanceImport.InvalidEventType:
      return 'pick official or club for the new event';
    case Code.AttendanceImport.AmbiguousUserPick:
      return 'pick a member for that row before continuing';

    // Cash App import
    case Code.CashAppImport.CsvEmpty:
      return "that csv doesn't have any rows — export your cash app activity and try again";
    case Code.CashAppImport.CsvMissingColumns: {
      const columns = typeof err.params?.columns === 'string' ? err.params.columns : null;
      return columns
        ? `that csv is missing columns: ${columns} — use the raw cash app export`
        : "couldn't read that csv — use the raw cash app export";
    }
    case Code.CashAppImport.CsvTooManyRows: {
      const maxRows = typeof err.params?.max_rows === 'number' ? err.params.max_rows : null;
      return maxRows !== null
        ? `that csv has more than ${String(maxRows)} rows — narrow the date range and try again`
        : 'that csv is too big — narrow the date range and try again';
    }

    // Attendance mark
    case Code.AttendanceMark.EventOrTitleRequired:
      return 'pick an existing event or give the new event a name and date';
    case Code.AttendanceMark.InvalidEventType:
      return 'pick official, club, or community for the new event';
    case Code.AttendanceMark.NoMembersSelected:
      return 'pick at least one member to mark attended';

    // Permission / rate
    case Code.Perm.Denied:
      return "you don't have permission to do that";
    case Code.Rate.Limited:
      return "you're going too fast — try again in a moment";

    // Page / Docs / JoinForm / Feedback / Notification
    case Code.Page.MembersOnly:
      return 'members only';
    case Code.Page.VisibilityInvalid:
      return 'invalid visibility setting';
    case Code.DevTools.NotFound:
      return "couldn't find that — try refreshing";
    case Code.Docs.FolderNotFound:
      return 'folder not found';
    case Code.Docs.ParentFolderNotFound:
      return 'parent folder not found';
    case Code.Docs.DocumentNotFound:
      return 'document not found';
    case Code.JoinForm.QuestionNotFound:
      return 'question not found';
    case Code.Feedback.NotConfigured:
      return 'feedback submission is not configured';
    case Code.Feedback.CreationFailed:
      return "couldn't submit feedback — try again";
    case Code.Notification.NotFound:
      return 'notification not found';
    case Code.CoHostInvite.NotFound:
      return 'invite not found';
    case Code.CoHostInvite.NotPending:
      return 'this invite has already been resolved';
    case Code.CoHostInvite.NotInvitee:
      return "this isn't your invite to respond to";
    case Code.CoHostInvite.NotHost:
      return 'only the event host can do that';
    case Code.CoHostInvite.NotRemovable:
      return "this invite can't be removed";
    case Code.CoHostInvite.WouldLeaveHostless:
      return "you can't step down — every event needs at least one host";
    case Code.CoHostInvite.EventIsPast:
      return "can't invite co-hosts to a past event";
    case Code.CoHostInvite.NotMember:
      return 'only members can be co-hosts';

    // Welcome template
    case Code.WelcomeTemplate.BodyRequired:
      return 'welcome message body is required';
    case Code.WelcomeTemplate.BodyTooLong: {
      const max = typeof err.params?.max_length === 'number' ? err.params.max_length : null;
      return max !== null
        ? `welcome message must be at most ${String(max)} characters`
        : 'welcome message is too long';
    }

    // Tentative approval message
    case Code.TentativeApprovalMessage.BodyRequired:
      return 'approval message body is required';
    case Code.TentativeApprovalMessage.BodyTooLong: {
      const max = typeof err.params?.max_length === 'number' ? err.params.max_length : null;
      return max !== null
        ? `approval message must be at most ${String(max)} characters`
        : 'approval message is too long';
    }

    // Member promotion message
    case Code.MemberPromotionMessage.BodyRequired:
      return 'member promotion message body is required';
    case Code.MemberPromotionMessage.BodyTooLong: {
      const max = typeof err.params?.max_length === 'number' ? err.params.max_length : null;
      return max !== null
        ? `member promotion message must be at most ${String(max)} characters`
        : 'member promotion message is too long';
    }

    // Member promotion email
    case Code.MemberPromotionEmail.BodyRequired:
      return 'member promotion email body is required';
    case Code.MemberPromotionEmail.BodyTooLong: {
      const max = typeof err.params?.max_length === 'number' ? err.params.max_length : null;
      return max !== null
        ? `member promotion email must be at most ${String(max)} characters`
        : 'member promotion email is too long';
    }

    // Rejection email
    case Code.RejectionEmail.BodyRequired:
      return 'rejection email body is required';
    case Code.RejectionEmail.BodyTooLong: {
      const max = typeof err.params?.max_length === 'number' ? err.params.max_length : null;
      return max !== null
        ? `rejection email must be at most ${String(max)} characters`
        : 'rejection email is too long';
    }
    case Code.RejectionEmail.ReasonTextRequired:
      return 'each reason needs email text';
    case Code.RejectionEmail.ReasonTextTooLong: {
      const max = typeof err.params?.max_length === 'number' ? err.params.max_length : null;
      return max !== null
        ? `reason text must be at most ${String(max)} characters`
        : 'reason text is too long';
    }

    case Code.Connect.RequestNotFound:
      return 'this connection request was not found';
    case Code.Connect.RequestExpired:
      return 'this connection request has expired, go back to your ai app and try connecting again';
    case Code.Connect.RequestAlreadyUsed:
      return 'this connection request was already used';
    case Code.Connect.AppNotFound:
      return 'connected app not found';

    // Generic (FE-only, emitted for Pydantic errors without a ValidationException)
    case Code.Generic.FieldRequired:
      return err.field ? `${err.field.replace(/_/g, ' ')} is required` : 'this field is required';
    case Code.Generic.FieldInvalid:
      return err.field ? `${err.field.replace(/_/g, ' ')} is not valid` : 'this field is not valid';

    default:
      return assertNever(code);
  }
}

// If TS flags this line with "Argument of type 'X' is not assignable to
// parameter of type 'never'", the named code has no case in the switch above.
// Add one, then re-run `pnpm typecheck`.
function assertNever(code: never): never {
  throw new Error(`unhandled validation code: ${String(code)}`);
}

/** Join multiple field errors into a single human-readable sentence. */
export function messagesFromFieldErrors(errors: FieldError[]): string {
  const msgs = errors.map(messageForCode);
  // Dedup while preserving order — backend sometimes emits two errors for
  // the same underlying mistake (type coerce + validator).
  const seen = new Set<string>();
  const uniq = msgs.filter((m) => {
    if (seen.has(m)) return false;
    seen.add(m);
    return true;
  });
  return uniq.join(' · ');
}

function isKnownCode(code: string): code is KnownCode {
  return KNOWN_CODES.has(code);
}

const KNOWN_CODES: ReadonlySet<string> = new Set<string>([...collectCodeValues(Code)]);

function collectCodeValues(tree: Record<string, unknown>): string[] {
  const out: string[] = [];
  for (const value of Object.values(tree)) {
    if (typeof value === 'string') {
      out.push(value);
    } else if (value && typeof value === 'object') {
      out.push(...collectCodeValues(value as Record<string, unknown>));
    }
  }
  return out;
}

function extractStringArray(params: Record<string, unknown> | undefined, key: string): string[] {
  const value = params?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}
